// Tomläges-CTA: "Hittar du ingen tid? Ställ dig i kö" (009 / M2).
//
// Kön fanns redan, men bara ovanpå NÅGON ANNANS BOKNING i tidslinjen —
// BookingGrid loopar över bokningar, inte över lediga luckor, så en full lucka
// hade inget att hänga etiketten på. I Snabboka och Auto-boka mötte kunden
// i stället döda `disabled`-rader märkta "Fullt" utan någon utväg alls.
//
// FLAGG-GATAD (L21): renderas bara när hallens `waitlist` är PÅ. Före 009 gatade
// flaggan BARA adminytan — spelarvägen var helt ogatad, så en hall med flaggan
// av kunde ändå samla köposter som ingen admin hade en sida att se dem på.
//
// UTLOGGADE ser knappen men skapar ingen post: RLS kräver auth.uid() OCH
// medlemskap i orgen. Klick leder till inloggning i stället för till ett tyst fel.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { buildWaitlistRows, localDateKey, type WaitlistCourt } from "@/lib/waitlistEntries";
import { cn } from "@/lib/utils";

interface WaitlistCtaProps {
  /** Banor kön ska gälla — Väg A skapar en post per bana. */
  courts: ReadonlyArray<WaitlistCourt>;
  date: Date;
  /** "HH:mm". */
  startTime: string;
  durationMinutes: number;
  /** Kompakt variant för en rad i en slot-lista. */
  compact?: boolean;
  className?: string;
}

export default function WaitlistCta({
  courts,
  date,
  startTime,
  durationMinutes,
  compact = false,
  className,
}: WaitlistCtaProps) {
  const { t } = useTranslation("booking");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [queued, setQueued] = useState(false);

  // L21: enforcement-läsningen. Utan den är toggeln bara dekoration.
  if (!isEnabled("waitlist")) return null;
  if (courts.length === 0) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!user) {
      // Ingen tyst miss: skicka kunden dit hen kan logga in.
      navigate("/auth");
      return;
    }
    if (!organization?.id) return;

    setSaving(true);
    try {
      const dateKey = localDateKey(date);
      const start = `${startTime}:00`;

      // Redan i kön på den här tiden? Kolla innan vi skapar N nya rader.
      const { data: existing } = await supabase
        .from("waitlist")
        .select("id")
        .eq("user_id", user.id)
        .eq("date", dateKey)
        .eq("start_time", start)
        .in("status", ["waiting", "offered"])
        .limit(1);

      if (existing && existing.length > 0) {
        toast.info(t("alreadyInQueue"));
        setQueued(true);
        return;
      }

      const rows = buildWaitlistRows({
        organizationId: organization.id,
        userId: user.id,
        courts,
        date,
        startTime,
        durationMinutes,
      });

      const { error } = await supabase.from("waitlist").insert(rows);
      if (error) {
        console.error("[waitlist] kunde inte skapa köposter:", error);
        toast.error(t("queueFailed"));
        return;
      }

      setQueued(true);
      toast.success(t("queueJoined"));
    } finally {
      setSaving(false);
    }
  };

  if (queued) {
    return (
      <span className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground", className)}>
        <Check className="h-3.5 w-3.5" />
        {t("queueJoinedShort")}
      </span>
    );
  }

  return (
    <Button
      type="button"
      variant={compact ? "ghost" : "outline"}
      size={compact ? "sm" : "default"}
      onClick={handleClick}
      disabled={saving}
      data-testid="waitlist-cta"
      className={cn("gap-1.5", compact && "h-auto py-1 px-2 text-xs", className)}
    >
      <Clock className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {compact ? t("queueMeShort") : t("queueMe")}
    </Button>
  );
}
